import React from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { getDatabase, ref, get, push, update } from 'firebase/database';
import { getBookingByID, getCurrentUserDetails} from './utils';
import { notifyOwnerTipReceived } from './utils/userNotifications';
import dayjs from 'dayjs';
import { useAuth } from '../contexts/AuthContext';
import { Alert} from '@mui/material';
import { Col, Row } from 'react-bootstrap';
import { ThreeDots } from 'react-loader-spinner';

const TIP_STRIPE_FEE_RATE = 0.03;

const calculateTipAmounts = (tipAmountDollars) => {
  const tipAmountCents = Math.round(Number(tipAmountDollars) * 100);

  if (!Number.isFinite(tipAmountCents) || tipAmountCents <= 0) {
    return { tipAmountCents: 0, platformFeeCents: 0, ownerAmountCents: 0 };
  }

  const platformFeeCents = Math.round(tipAmountCents * TIP_STRIPE_FEE_RATE);
  const ownerAmountCents = tipAmountCents - platformFeeCents;

  return { tipAmountCents, platformFeeCents, ownerAmountCents };
};

const PaymentForm = (bookingId) => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const auth = useAuth();

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [bookingData, setBookingData] = useState([]);
  const [amount, setAmount] = useState();
  const BASE_URL = process.env.REACT_APP_BASE_URL; // Fetch the base URL from .env


  useEffect(() => {
    const fetchDetails = async () => {
      try {
        console.log('bookingDetails');
        console.log(bookingId.bookingId);
        const bookingDetails = await getBookingByID(bookingId.bookingId);
        if (!bookingDetails) {
          return;
        }
        setBookingData(bookingDetails);
      } catch (error) {
        console.error('Error fetching listing details:', error.message);
      }
    };
  
    fetchDetails();
  }, [bookingId]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');
  
    if (!stripe || !elements) {
      return;
    }

    const { tipAmountCents, platformFeeCents, ownerAmountCents } =
      calculateTipAmounts(amount);

    if (tipAmountCents <= 0) {
      setError('Please enter a valid tip amount.');
      setLoading(false);
      return;
    }
  
    const { token, error } = await stripe.createToken(elements.getElement(CardElement));
  
    if (error) {
      console.error(error);
      setError(error.message || "There was an error processing your card. Please try again.");
      setLoading(false);
      return;
    } else {

      try {
        const response = await fetch(`${BASE_URL}/capture-payment`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'Access-Control-Allow-Origin': BASE_URL,
          },
          mode: 'cors',
          body: JSON.stringify({
            tokenId: token.id,
            amount: tipAmountCents,
          }),
        });
  
        const result = await response.json();
        console.log('result: ', result);
  
        if (result.success) {

          const database = getDatabase();

          try {
            const paymentRef = ref(getDatabase(), 'payments');
            let paymentData = {};
            paymentData = {
                reason: 'Tip',
                ownerAmount: ownerAmountCents,
                totalAmount: tipAmountCents,
                paidAmount: tipAmountCents,
                adminCut: platformFeeCents,
                platformFee: platformFeeCents,
                dateTime: dayjs().format('dddd, MMMM D, YYYY HH:mm:ss'),
                transactionId: result.paymentIntent.id,
                userId: auth.currentUser.uid,
                owner: bookingData.owner,
                bookingId: bookingId.bookingId,
                status: 'completed',
                tripCompleted: true
            };
                        
              await push(paymentRef, paymentData);

            //   const renterData = await getCurrentUserDetails();
            //   const subjectEmail = 'Payment Successful';
            //   const renterName = renterData.firstName + ' ' + renterData.lastName;
            //   const emailBody = await BookingStatusUpdateEmailBody (renterName, bookingData.listingTitle, 'Paid')
            //   sendEmail (renterData.email, subjectEmail, emailBody);

            //   const ownerData = await getUserByID(bookingData.owner);
            //   const subjectOwnerEmail = 'Payment Successful';
            //   const ownerName = ownerData.firstName + ' ' + ownerData.lastName;
            //   const emailOwnerBody = await BookingStatusUpdateEmailBody (ownerName, bookingData.listingTitle, 'Paid')
            //   sendEmail (ownerData.email, subjectOwnerEmail, emailOwnerBody);

              const ownerMetaPayments = ref(database, `ownerMeta/${bookingData.owner}`)

              const previousOwnerPaymentsSnapshot = await get(ownerMetaPayments);


              if (previousOwnerPaymentsSnapshot.exists()) {

              const previousOwnerPayments = previousOwnerPaymentsSnapshot.val();

              const previousCurrentAmount = previousOwnerPayments.payments.currentAmount;
              const previousTotalAmount = previousOwnerPayments.payments.totalAmount;
              const ownerPayments = {
                currentAmount: parseInt(previousCurrentAmount, 10) + ownerAmountCents,
                totalAmount: parseInt(previousTotalAmount, 10) + ownerAmountCents,
              }
              await update(ownerMetaPayments, {payments: ownerPayments});
            }

            const renterData = await getCurrentUserDetails();
            const tipperName = renterData
              ? `${renterData.firstName || ""} ${renterData.lastName || ""}`.trim()
              : "";
            notifyOwnerTipReceived(
              bookingData.owner,
              bookingData.listingTitle,
              bookingId.bookingId,
              tipAmountCents / 100,
              tipperName
            ).catch((err) =>
              console.error("Failed to create owner tip notification:", err)
            );
            } catch (error) {
              console.log('Error saving data: ', error.message);
              setLoading(false);
            }
            navigate('/dashboard/thank-you');
            setLoading(false);
        }else if(result.error){
          console.error('Payment failed:', result.error);
          setError(result.error);
          setLoading(false);
        } else {
          console.error('Payment failed:');
          setError('Payment failed: ');
          setLoading(false);
        }
      } catch (error) {
        console.error('Error capturing payment:', error);
       setError(error);
        setLoading(false);
      }
    }
  };

  const { tipAmountCents, platformFeeCents, ownerAmountCents } =
    calculateTipAmounts(amount);
  const hasValidTipAmount = tipAmountCents > 0;

  return (

    <>
    <Row className='p-4' style={{width: '60%'}}>
    <div class="">
        <div class="mt-2">
            <div class="row">
                <div class="col-sm-4 d-flex align-items-center">
                <h3 class="mb-0 text-white">Amount</h3>
                </div>
                <div class="col-sm-8 d-flex gap-3">
                <input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className='form-control' style={{ fontSize: '24px', border: 'none' }} placeholder="$"/>
                </div>
            </div>
            <p className="text-white mt-3 mb-0" style={{ fontSize: '14px' }}>
              A 3% Stripe processing fee will be deducted from your tip.
              {hasValidTipAmount ? (
                <>
                  {" "}
                  The boat owner will receive $
                  {(ownerAmountCents / 100).toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}{" "}
                  (${(platformFeeCents / 100).toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}{" "}
                  Stripe fee).
                </>
              ) : null}
            </p>
            <hr/>
            </div>
        </div>
      <Col>
        <form className="d-flex flex-column" onSubmit={handleSubmit}>
          <div style={{ marginBottom: '20px' }}>
            {/* Style the CardElement */}
            <Row>
            </Row>
            <Row>
              <Col>
                <CardElement
                  className="form-control"
                  options={{
                    style: {
                      base: {
                        fontSize: '16px',
                        color: '#424770',
                        '::placeholder': {
                          color: '#aab7c4',
                        },
                      },
                      invalid: {
                        color: '#9e2146',
                      },
                    },
                  }}
                />
                {error && <Alert className='mt-3' severity='error'>{error}</Alert>}
              </Col>
            </Row>      
          </div>
          <button variant="primary" type="submit" className='btn btn-light text-dark m-1'>
            {loading ? 
            <ThreeDots
              visible={true}
              height="22"
              width="22"
              color="#fff"
              radius="20"
              ariaLabel="three-dots-loading"
              wrapperStyle={{}}
              wrapperClass=""
              /> : 'Tip'}
            </button>
        </form>
      </Col>
      
    </Row>
    </>
    
  );
};

export default PaymentForm;
